import { Component } from 'preact';

import { COMPOSITION_TYPES } from 'constants/annotations';
import * as is from 'utilities/is';

// need to store on first execution of script
// async access of `document.currentScript` may be empty
const CURRENT_SCRIPT = document.currentScript;

export const inject = (container, el) => {
    return new Promise((resolve, reject) => {
        const observer = new MutationObserver((list, observer) => {
            const childChanged = list.some(change => change.type === 'childList');

            if (childChanged) {
                observer.disconnect();
                resolve();
            }

        });

        observer.observe(container, { childList: true });
        container.appendChild(el);
    });
};

export const insideIFrame = () => window.parent !== window;

export const safelyGetBCR = (element) => element && element.getBoundingClientRect && element.getBoundingClientRect() || {};

export const collectStyles = ({ styles, element, state = {} }) => {
    let elementStyles = {},
        stateStyles = {},
        possibleStyles;

    if (styles) {
        if (element === COMPOSITION_TYPES.ROOT) {
            elementStyles = styles[element] || styles;
        } else if (styles[element]) {
            elementStyles = styles[element];
        }

        possibleStyles = Object.keys(state).filter(k => state[k]).map(k => `${element}.${k}`);
        stateStyles = possibleStyles.reduce((collected, s) => ({ ...collected, ...styles[s] }), {});
    }

    return { ...elementStyles, ...stateStyles };
};

export const applyStyles = (el, styles = {}) => Object.keys(styles).forEach(key => el.style[key] = styles[key]);

export const getCurrentScriptSource = () => {
    let currentScript = '';

    if (CURRENT_SCRIPT) {
        currentScript = CURRENT_SCRIPT.src;
    } else {
        const lastScript = Array.from(document.scripts).pop();
        currentScript = lastScript ? lastScript.src : '';
    }

    return currentScript;
};

export const findEmbed = id => document.querySelector(`[data-embed-id="${id}"]`);
export const findEmbedFromWindow = (contentWindow, selector = 'iframe') => (
    Array
        .from(document.querySelectorAll(selector))
        .find(el => el.contentWindow === contentWindow)
);

export const keyHandler = (keyValue, delegatedHandler) => e => {
    const keyValues = is.array(keyValue) ? keyValue : [keyValue];
    if (keyValues.includes(e.keyCode) && is.fn(delegatedHandler)) {
        e.preventDefault();
        delegatedHandler(e);
    }
};

export class Hover extends Component {
    constructor(props) {
        super(props);
        this.timeout = null;

        if (is.fn(props.cancelHoverProvider)) {
            props.cancelHoverProvider(this.onCancel);
        }
    }

    onMouseEnter = e => {
        this.timeout = setTimeout(this.applyAction, this.duration);
    }

    onMouseLeave = e => {
        this.reset();

        if (is.fn(this.props.onLeave)) {
            this.props.onLeave();
        }
    }

    onCancel = () => {
        this.reset();
    }

    applyAction = e => {
        this.reset();

        if (is.fn(this.props.onElapsed)) {
            this.props.onElapsed();
        }
    }

    reset() {
        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }
    }

    render() {
        return (
            <span onMouseEnter={ this.onMouseEnter } onMouseLeave={ this.onMouseLeave }>
                { this.props.children }
            </span>
        );
    }

    get duration() {
        return this.props.duration || 1000;
    }
}

export const documentReady = () => {
    if (
        document.readyState === 'complete'    ||
        document.readyState === 'loaded'      ||
        document.readyState === 'interactive'
    ) {
        return Promise.resolve();
    }

    return new Promise(resolve => {
        window.addEventListener('DOMContentLoaded', function onDOMLoaded() {
            window.removeEventListener('DOMContentLoaded', onDOMLoaded);
            resolve();
        })
    });
}

// same as `inject` but doesn't return until document is ready
// useful for testing custom tag embed that has to wait until document ready
// to get reference to child elements
export const injectReady = (...args) => inject(...args).then(documentReady);